import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyrightIcon from '@mui/icons-material/Copyright';
import '../shared.css';

const CompanyEdit = ({ match }) => {
    const recordId = match.params.id;
    const history  = useHistory();
    const { isAuthenticated, companyId, logout, token } = useAuth();

    const [pinCodes, setPinCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [dateValidationError, setDateValidationError] = useState('');
    const [slabValidationError, setSlabValidationError] = useState('');

    // Slab dialog state
    const [showSlabDialog, setShowSlabDialog]     = useState(false);
    const [editingSlabIndex, setEditingSlabIndex] = useState(null);
    const [currentSlab, setCurrentSlab]           = useState({ DiscountPercentage: '', MinimumQuantity: '' });

    const [formData, setFormData] = useState({
        Name: '', CompanyCode: '', ContactNumber1: '', ContactNumber2: '',
        Email: '', GSTNumber: '', AddressLine1: '', AddressLine2: '',
        PinCode: '', City: '', District: '', State: '', Country: '',
        BillRemarks: '', ReprintPassword: '', PaymentQRUPIId: '', PaymentQRUPIName: '',
        PromoDiscountStartDate: '', PromoDiscountEndDate: '',
        PromoDiscountSlab: [],
    });

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, token, logout]);

    // Load pin codes
    useEffect(() => {
        fetchData('PinCodeApi', `/1`, token)
            .then(res => { if (res.responseCode === '1111') setPinCodes(res.response_data); })
            .catch(err => console.error('Error fetching PIN codes:', err));
    }, []);

    // Load company data
    useEffect(() => {
        setIsLoading(true);
        fetchData('CompanyApi', `/CompanyDetails/${recordId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const responseData = response.response_data;
                    let companyData    = responseData.Company ?? responseData;
                    let promoSlab      = responseData.PromoDiscountSlab ?? companyData.PromoDiscountSlab ?? [];

                    if (!companyData.hasOwnProperty('PromoDiscountStartDate')) companyData.PromoDiscountStartDate = '';
                    if (!companyData.hasOwnProperty('PromoDiscountEndDate'))   companyData.PromoDiscountEndDate   = '';

                    // Migrate old PromoDiscountQuantity → slab
                    if (promoSlab.length === 0 && companyData.PromoDiscountQuantity) {
                        promoSlab = [{ DiscountPercentage: 0, MinimumQuantity: parseInt(companyData.PromoDiscountQuantity) }];
                    }

                    if (companyData.PromoDiscountStartDate) companyData.PromoDiscountStartDate = formatDateForInput(companyData.PromoDiscountStartDate);
                    if (companyData.PromoDiscountEndDate)   companyData.PromoDiscountEndDate   = formatDateForInput(companyData.PromoDiscountEndDate);

                    setFormData({ ...companyData, PromoDiscountSlab: promoSlab });
                }
            })
            .catch(err => console.error('Error fetching Company Details:', err))
            .finally(() => setIsLoading(false));
    }, []);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const d = new Date(dateString);
            if (isNaN(d.getTime())) return '';
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        } catch { return ''; }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'PromoDiscountStartDate' || name === 'PromoDiscountEndDate') {
            validateDates(name, value);
        }
    };

    const handlePinCodeChange = (value) => {
        if (value.length === 6) {
            const found = pinCodes.find(p => p.Postcode === value);
            if (found) setFormData(prev => ({ ...prev, City: found.City, District: found.District, State: found.State, Country: found.Country }));
        }
    };

    const validateDates = (changedField, changedValue) => {
        const start = changedField === 'PromoDiscountStartDate' ? changedValue : formData.PromoDiscountStartDate;
        const end   = changedField === 'PromoDiscountEndDate'   ? changedValue : formData.PromoDiscountEndDate;
        setDateValidationError('');
        if (start && end) {
            const s = new Date(start), e = new Date(end);
            if (isNaN(s.getTime()) || isNaN(e.getTime())) { setDateValidationError('Please enter valid dates'); return; }
            if (e <= s) setDateValidationError('End date must be after start date');
        }
    };

    // ── Slab helpers ────────────────────────────────────────────
    const openSlabDialog = (idx = null) => {
        setEditingSlabIndex(idx);
        setCurrentSlab(idx !== null ? { ...formData.PromoDiscountSlab[idx] } : { DiscountPercentage: '', MinimumQuantity: '' });
        setSlabValidationError('');
        setShowSlabDialog(true);
    };

    const closeSlabDialog = () => {
        setShowSlabDialog(false);
        setEditingSlabIndex(null);
        setCurrentSlab({ DiscountPercentage: '', MinimumQuantity: '' });
        setSlabValidationError('');
    };

    const saveSlab = () => {
        const disc = parseInt(currentSlab.DiscountPercentage);
        const qty  = parseInt(currentSlab.MinimumQuantity);
        if (!currentSlab.DiscountPercentage || !currentSlab.MinimumQuantity) { setSlabValidationError('Both fields are required'); return; }
        if (isNaN(disc) || disc < 1 || disc > 100) { setSlabValidationError('Discount must be between 1 and 100'); return; }
        if (isNaN(qty)  || qty  < 1)               { setSlabValidationError('Minimum quantity must be greater than 0'); return; }
        const duplicate = formData.PromoDiscountSlab.findIndex((s, i) => i !== editingSlabIndex && parseInt(s.MinimumQuantity) === qty);
        if (duplicate !== -1) { setSlabValidationError(`Minimum quantity ${qty} already exists`); return; }

        const updated = [...formData.PromoDiscountSlab];
        const slabData = { DiscountPercentage: disc, MinimumQuantity: qty };
        if (editingSlabIndex !== null) updated[editingSlabIndex] = slabData;
        else updated.push(slabData);
        updated.sort((a, b) => a.MinimumQuantity - b.MinimumQuantity);
        setFormData(prev => ({ ...prev, PromoDiscountSlab: updated }));
        closeSlabDialog();
    };

    const deleteSlab = (idx) => {
        const updated = [...formData.PromoDiscountSlab];
        updated.splice(idx, 1);
        setFormData(prev => ({ ...prev, PromoDiscountSlab: updated }));
    };

    // ── Submit ──────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        if (dateValidationError) return;
        setIsLoading(true);

        const Company = { ...formData };
        const PromoDiscountSlab = Company.PromoDiscountSlab || [];
        delete Company.PromoDiscountSlab;
        delete Company.PromoDiscountQuantity;

        try {
            Company.PromoDiscountStartDate = Company.PromoDiscountStartDate?.trim()
                ? new Date(Company.PromoDiscountStartDate + 'T00:00:00').toISOString() : null;
            Company.PromoDiscountEndDate = Company.PromoDiscountEndDate?.trim()
                ? new Date(Company.PromoDiscountEndDate + 'T00:00:00').toISOString() : null;
        } catch {
            setDateValidationError('Invalid date format. Please check your promotional dates.');
            setIsLoading(false);
            return;
        }

        updateData('CompanyApi', `${recordId}`, {
            Company: { ...Company, UpdatedBy: 1 },
            PromoDiscountSlab: PromoDiscountSlab.map(s => ({
                DiscountPercentage: parseInt(s.DiscountPercentage),
                MinimumQuantity:    parseInt(s.MinimumQuantity),
            })),
        }, token)
            .then(() => history.push('/Home'))
            .catch((err) => {
                console.error('Error updating record:', err);
                setDateValidationError('Error updating company information. Please try again.');
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div className="pg-container">
            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Promotional Discount Settings</h5>
                <div className="pg-action-row">
                    <button className="pg-btn pg-btn-success" onClick={handleSubmit}
                        disabled={isLoading || !!dateValidationError}>
                        {isLoading ? <CircularProgress size={13} color="inherit" /> : <SaveIcon style={{ fontSize: 13 }} />}
                        &nbsp;{isLoading ? 'Updating…' : 'Update'}
                    </button>
                    <Link to="/Home" className="pg-btn pg-btn-danger">
                        <CancelIcon style={{ fontSize: 13 }} /> Cancel
                    </Link>
                </div>
            </div>

            {dateValidationError && <div className="pg-alert pg-alert-error" style={{ marginBottom: 6 }}>{dateValidationError}</div>}

            <form onSubmit={handleSubmit}>
                <div className="pg-card" style={{ padding: '10px 14px 10px' }}>

                    {/* ── Dates + Add button on one row ── */}
                    <div style={styles.topRow}>
                        <div className="pg-field" style={{ flex: '0 0 160px' }}>
                            <label className="pg-label">Start Date</label>
                            <input className="pg-input" type="date" name="PromoDiscountStartDate"
                                value={formData.PromoDiscountStartDate} onChange={handleChange} />
                        </div>
                        <div className="pg-field" style={{ flex: '0 0 160px' }}>
                            <label className="pg-label">End Date</label>
                            <input className="pg-input" type="date" name="PromoDiscountEndDate"
                                value={formData.PromoDiscountEndDate} onChange={handleChange} />
                        </div>
                        <div style={{ flex: 1 }} />
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                            <span style={styles.slabLabel}>
                                Discount Slabs
                                <span style={styles.badge}>{formData.PromoDiscountSlab?.length || 0}</span>
                            </span>
                            <button type="button" className="pg-btn pg-btn-primary" onClick={() => openSlabDialog()}>
                                <AddIcon style={{ fontSize: 13 }} /> Add Slab
                            </button>
                        </div>
                    </div>

                    {/* ── Slab table ── */}
                    <div style={{ marginTop: 8 }}>
                        {formData.PromoDiscountSlab?.length > 0 ? (
                            <div className="pg-table-wrap" style={{ maxHeight: 220 }}>
                                <table className="pg-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th style={{ textAlign: 'right' }}>Min Qty</th>
                                            <th style={{ textAlign: 'right' }}>Discount %</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.PromoDiscountSlab.map((slab, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td style={{ textAlign: 'right' }}>{slab.MinimumQuantity}</td>
                                                <td style={{ textAlign: 'right' }}>{slab.DiscountPercentage}%</td>
                                                <td className="actions-cell">
                                                    <button type="button" style={styles.actionBtn} title="Edit" onClick={() => openSlabDialog(idx)}>
                                                        <EditIcon style={{ fontSize: 14, color: '#1976d2' }} />
                                                    </button>
                                                    <button type="button" style={styles.actionBtn} title="Delete" onClick={() => deleteSlab(idx)}>
                                                        <DeleteIcon style={{ fontSize: 14, color: '#dc2626' }} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={styles.emptySlabs}>
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>No slabs configured — click Add Slab to begin</span>
                            </div>
                        )}
                    </div>
                </div>
            </form>

            {/* ── Slab Add/Edit Dialog ── */}
            <Dialog open={showSlabDialog} onClose={closeSlabDialog} maxWidth="xs" fullWidth>
                <DialogTitle style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 700, paddingBottom: 4 }}>
                    {editingSlabIndex !== null ? 'Edit Slab' : 'Add Slab'}
                </DialogTitle>
                <DialogContent style={{ paddingTop: 8 }}>
                    <div style={{ display: 'flex', gap: 12, paddingTop: 10 }}>
                        <div className="pg-field" style={{ flex: 1 }}>
                            <label className="pg-label" style={{ background: '#fff' }}>Discount % <span className="req">*</span></label>
                            <input className="pg-input text-right" type="number" min="1" max="100"
                                value={currentSlab.DiscountPercentage}
                                onChange={e => setCurrentSlab(p => ({ ...p, DiscountPercentage: e.target.value }))}
                                placeholder="1–100" />
                        </div>
                        <div className="pg-field" style={{ flex: 1 }}>
                            <label className="pg-label" style={{ background: '#fff' }}>Min Qty <span className="req">*</span></label>
                            <input className="pg-input text-right" type="number" min="1"
                                value={currentSlab.MinimumQuantity}
                                onChange={e => setCurrentSlab(p => ({ ...p, MinimumQuantity: e.target.value }))}
                                placeholder="e.g. 5" />
                        </div>
                    </div>
                    {slabValidationError && (
                        <div className="pg-alert pg-alert-error" style={{ marginTop: 8 }}>{slabValidationError}</div>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '4px 16px 12px', gap: 6 }}>
                    <button className="pg-btn pg-btn-danger" onClick={closeSlabDialog}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={saveSlab}>
                        {editingSlabIndex !== null ? 'Update' : 'Add'}
                    </button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
};

const styles = {
    topRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        flexWrap: 'wrap',
        paddingTop: 6,
    },
    slabLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: '#1c2a3a',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)',
        color: '#fff',
        borderRadius: 10,
        fontSize: 10,
        fontWeight: 700,
        padding: '1px 6px',
        minWidth: 18,
    },
    actionBtn: {
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '1px 3px', display: 'inline-flex', alignItems: 'center',
    },
    emptySlabs: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 0',
        border: '1.5px dashed rgba(200,212,224,0.7)',
        borderRadius: 6,
    },
};

export default CompanyEdit;
